// @systeme: interface
// Une ligne de l'écran Aujourd'hui : une grande coche, le titre, l'heure s'il y en a une,
// et un menu discret (reporter, corriger, supprimer, signaler).
import { useState, type MouseEvent } from 'react';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Check, Circle, CircleCheck, EllipsisVertical } from 'lucide-react';
import type { Element } from '@foyer/shared';
import { formaterHeure, formaterJourCourt } from '../lib/dates';
import { couleurs, tailles } from '../theme/theme';

interface Props {
  element: Element;
  onFaire(): void;
  onAnnulerFait?(): void;
  onCorriger(): void;
  onReporter(): void;
  onSupprimer(): void;
  onSignaler(): void;
  /** Préparer à rebours (anniversaire, rentrée…) : seulement pour un élément daté qui n'est pas lui-même une étape. */
  onPreparer?(): void;
  /** Une précision sous le titre, donnée par l'écran (la date d'une étape, par exemple). */
  sousTitre?: string;
}

export function LigneElement({
  element,
  onFaire,
  onAnnulerFait,
  onCorriger,
  onReporter,
  onSupprimer,
  onSignaler,
  onPreparer,
  sousTitre,
}: Props) {
  const [ancre, setAncre] = useState<HTMLElement | null>(null);
  const fait = element.statut === 'fait';
  const heure =
    element.debutLe && !element.journeeEntiere ? formaterHeure(element.debutLe) : undefined;
  const limite = element.limiteLe ? `avant le ${formaterJourCourt(element.limiteLe)}` : undefined;
  const fermer = () => setAncre(null);
  const action = (f: () => void) => () => {
    fermer();
    f();
  };

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <IconButton
          edge="end"
          aria-label={`Options pour ${element.titre}`}
          onClick={(e: MouseEvent<HTMLElement>) => setAncre(e.currentTarget)}
        >
          <EllipsisVertical size={22} />
        </IconButton>
      }
      sx={{
        bgcolor: couleurs.blanc,
        borderRadius: `${tailles.rayon}px`,
        mb: 1,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <IconButton
        aria-label={fait ? `Décocher ${element.titre}` : `Fait : ${element.titre}`}
        onClick={fait ? onAnnulerFait : onFaire}
        sx={{ ml: 0.5, color: fait ? couleurs.reussite : couleurs.sauge }}
      >
        {fait ? <CircleCheck size={30} /> : <Circle size={30} />}
      </IconButton>
      <ListItemButton onClick={onCorriger} sx={{ minHeight: tailles.cible + 8, pl: 0.5 }}>
        <ListItemText
          primary={element.titre}
          secondary={
            [
              sousTitre,
              heure,
              limite,
              element.visibilite === 'prive' ? 'rien que pour toi' : undefined,
            ]
              .filter(Boolean)
              .join(' · ') || undefined
          }
          primaryTypographyProps={{
            sx: {
              textDecoration: fait ? 'line-through' : 'none',
              color: fait ? 'text.secondary' : 'text.primary',
            },
          }}
        />
      </ListItemButton>
      <Menu anchorEl={ancre} open={ancre !== null} onClose={fermer}>
        {!fait && (
          <MenuItem onClick={action(onFaire)}>
            <Check size={18} style={{ marginRight: 10 }} />
            Fait
          </MenuItem>
        )}
        <MenuItem onClick={action(onReporter)}>Reporter</MenuItem>
        <MenuItem onClick={action(onCorriger)}>Corriger</MenuItem>
        {onPreparer && element.debutLe && element.parentId === null && (
          <MenuItem onClick={action(onPreparer)}>Préparer…</MenuItem>
        )}
        <MenuItem onClick={action(onSignaler)}>Un souci ?</MenuItem>
        <MenuItem onClick={action(onSupprimer)} sx={{ color: 'error.main' }}>
          Supprimer
        </MenuItem>
      </Menu>
    </ListItem>
  );
}
